import { useState, useRef } from "react";

export const C = {
  bg0: "rgba(255,255,255,0.03)", bg1: "rgba(255,255,255,0.05)",
  bg2: "rgba(255,255,255,0.05)", bg3: "rgba(255,255,255,0.08)",
  border: "rgba(255,255,255,0.07)", accent: "#f97316",
  text: "#f1f5f9", muted: "#64748b", faint: "#334155",
};

export function Label({ children, required }) {
  return (
    <label style={{ display:"block", fontSize:"0.7rem", fontWeight:700,
      letterSpacing:"0.7px", textTransform:"uppercase", marginBottom:6, color:C.muted }}>
      {children}{required && <span style={{ color:"#f87171", marginLeft:3 }}>*</span>}
    </label>
  );
}

export function Input({ style={}, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <input {...props}
      onFocus={e=>{setFocus(true);props.onFocus?.(e);}}
      onBlur={e=>{setFocus(false);props.onBlur?.(e);}}
      style={{ width:"100%", background:"rgba(255,255,255,0.06)", borderRadius:8,
        border:`1px solid ${focus?C.accent:C.border}`,
        padding:"10px 14px", color:"#f1f5f9", fontSize:"0.88rem",
        outline:"none", boxSizing:"border-box",
        boxShadow: focus?"0 0 0 3px rgba(249,115,22,0.08)":"none",
        transition:"border-color 0.15s", ...style }}/>
  );
}

export function Btn({ children, onClick, variant="primary", disabled, small }) {
  const [h, setH] = useState(false);
  const cfg = {
    primary: { bg:h?"#ea6b10":"#f97316", color:"#fff", border:"#f97316" },
    ghost:   { bg:h?"rgba(255,255,255,0.05)":"transparent", color:C.muted, border:C.border },
    danger:  { bg:h?"rgba(239,68,68,0.18)":"rgba(239,68,68,0.08)", color:"#f87171", border:"rgba(239,68,68,0.3)" },
  }[variant]??{};
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseOver={()=>setH(true)} onMouseOut={()=>setH(false)}
      style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:8,
        color:cfg.color, padding:small?"5px 12px":"9px 18px",
        fontSize:small?"0.78rem":"0.85rem", fontWeight:600,
        cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.5:1,
        transition:"all 0.15s", display:"inline-flex", alignItems:"center",
        gap:7, whiteSpace:"nowrap", fontFamily:"'Inter',sans-serif" }}>
      {children}
    </button>
  );
}

export function Toast({ msg, type }) {
  if (!msg) return null;
  const ok = type !== "error";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10,
      background:ok?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)",
      border:`1px solid ${ok?"rgba(16,185,129,0.25)":"rgba(239,68,68,0.25)"}`,
      borderLeft:`3px solid ${ok?"#10b981":"#ef4444"}`,
      borderRadius:8, padding:"10px 14px", marginBottom:16,
      color:ok?"#6ee7b7":"#fca5a5", fontSize:"0.84rem", fontWeight:500 }}>
      <i className={`fa-solid ${ok?"fa-circle-check":"fa-circle-exclamation"}`} style={{flexShrink:0}}/>
      {msg}
    </div>
  );
}

export function ImageCard({ label, icon, hint, currentUrl, onFile, onClear, bgLight=false }) {
  const ref = useRef();
  return (
    <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:10, padding:18 }}>
      <p style={{ margin:"0 0 14px", fontWeight:600, color:C.text,
        fontSize:"0.85rem", display:"flex", alignItems:"center", gap:8 }}>
        <i className={`fa-solid ${icon}`} style={{color:C.accent,fontSize:"0.8rem"}}/>
        {label}
      </p>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        <div onClick={()=>ref.current.click()}
          style={{ width:68, height:68, borderRadius:10, flexShrink:0,
            border:`2px dashed ${C.faint}`, background:bgLight?"#f8fafc":C.bg0,
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", overflow:"hidden", transition:"border-color 0.15s" }}
          onMouseOver={e=>e.currentTarget.style.borderColor=C.accent}
          onMouseOut={e=>e.currentTarget.style.borderColor=C.faint}>
          {currentUrl
            ? <img src={currentUrl} alt={label}
                style={{width:"100%",height:"100%",objectFit:"contain",padding:bgLight?4:0}}/>
            : <i className={`fa-solid ${icon}`}
                 style={{fontSize:"1.4rem",color:bgLight?"#94a3b8":C.faint}}/>}
        </div>
        <div>
          <p style={{margin:"0 0 4px",fontSize:"0.76rem",color:C.muted}}>{hint}</p>
          {currentUrl && (
            <p style={{margin:"0 0 6px",fontSize:"0.72rem",color:"#10b981",
              display:"flex",alignItems:"center",gap:4}}>
              <i className="fa-solid fa-circle-check"/>Imagen cargada
            </p>
          )}
          <div style={{display:"flex",gap:6}}>
            <Btn small onClick={()=>ref.current.click()}>
              <i className="fa-solid fa-upload"/>{currentUrl?"Cambiar":"Subir"}
            </Btn>
            {currentUrl && (
              <Btn small variant="danger" onClick={onClear}>
                <i className="fa-solid fa-trash"/>Quitar
              </Btn>
            )}
          </div>
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*" style={{display:"none"}}
        onChange={e=>{const f=e.target.files[0];if(f)onFile(f);}}/>
    </div>
  );
}

/**
 * compressImage — reduce el tamaño de un archivo de imagen antes de subirlo.
 * - PNG: se redimensiona manteniendo transparencia.
 * - JPG/WebP/etc: se convierte a JPEG con calidad reducida.
 * - SVG o archivos < 200 KB: se devuelven sin cambios.
 */
export async function compressImage(file, maxWidth = 1200, maxHeight = 900) {
  return new Promise((resolve) => {
    if (!file || file.type === "image/svg+xml" || file.size < 200 * 1024) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement("canvas");
        canvas.width  = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        const keepPng = file.type === "image/png";
        if (!keepPng) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(img, 0, 0, width, height);

        const mimeOut = keepPng ? "image/png" : "image/jpeg";
        const quality = keepPng ? undefined : 0.85;

        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) { resolve(file); return; }
            const ext     = keepPng ? "png" : "jpg";
            const newName = file.name.replace(/\.[^.]+$/, `.${ext}`);
            resolve(new File([blob], newName, { type: mimeOut, lastModified: Date.now() }));
          },
          mimeOut,
          quality,
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}
